import { Device, Call } from "@twilio/voice-sdk";
import { CallResponse, TranscriptionStatus } from "../types/call.types";
import axios from "../config/axios";


interface CallStatus {
  isInitialized: boolean;
  isConnected: boolean;
  callSid: string | null;
  status: "queued" | "ringing" | "in-progress" | "completed" | "failed";
  duration: string | null;
  error: string | null;
}

// Error code mapping for better error messages
const TWILIO_ERROR_CODES: { [key: string]: string } = {
  31005:
    "Error connecting to Twilio. This usually means the TwiML app is not properly configured.",
  31204: "Invalid authentication token",
  31205: "Authentication token expired",
  31208: "WebRTC errors/Permission denied",
  31481: "Invalid number",
  31486: "Phone number is not reachable",
};

interface IncomingCallHandlers {
  onIncomingCall: (from: string) => void;
  onCallAccepted: () => void;
  onCallDisconnected: () => void;
  onError: (error: Error) => void;
}

// API Constants (env-configurable)
const API_CONFIG = {
  fromNumber: "",
  baseUrl: process.env.REACT_APP_BASE_URL || "",
};

class CallAPI {
  private device: Device | null = null;
  private activeCall: Call | null = null;
  private callStartTime: number | null = null;
  private baseUrl: string;
  private isInitializing: boolean = false;
  private initializationPromise: Promise<void> | null = null;
  private fromNumber: string;
  private incomingCallHandlers: Set<IncomingCallHandlers> = new Set();

  constructor() {
    this.baseUrl = API_CONFIG.baseUrl;
    this.fromNumber = API_CONFIG.fromNumber;
    // Remove automatic initialization, only initialize when needed
    // this.initializeDevice();
  }

  private async initializeDevice() {
    // Don't initialize if we're already initializing or the device is already registered
    if (
      this.isInitializing ||
      (this.device && this.device.state === "registered")
    ) {
      return;
    }

    // Check if we're authenticated before attempting to fetch a token
    if (!localStorage.getItem("token")) {
      console.log("Not authenticated, skipping Twilio device initialization");
      return;
    }

    this.isInitializing = true;
    try {
      console.log("Fetching Twilio token...");
      const response = await axios.get('/api/calls/token');

      if (!response.data.token) {
        throw new Error("No token received from server");
      }

      console.log("Setting up device with token...");
      await this.setupDevice(response.data.token);
      console.log("Device setup complete");
    } catch (error) {
      console.error("Error initializing device:", error);
      throw error;
    } finally {
      this.isInitializing = false;
    }
  }

  async setupDevice(token: string) {
    if (this.device) {
      this.device.destroy();
    }

    this.device = new Device(token, {
      allowIncomingWhileBusy: true,
      closeProtection: true,
    });

    this.device.on("incoming", this.handleIncomingCall.bind(this));
    this.device.on("error", this.handleDeviceError.bind(this));

    await this.device.register();
    console.log("Device registered successfully");
  }

  private handleIncomingCall(call: Call) {
    const from = call.parameters.From || "Unknown";
    // Log CallSid for incoming calls as well
    console.log(
      "Incoming call received with CallSid:",
      call.parameters.CallSid
    );
    console.log(
      "You can use this CallSid to download the audio recording once the call is complete."
    );

    this.incomingCallHandlers.forEach((handler) => {
      handler.onIncomingCall(from);
    });

    call.on("accept", () => {
      this.activeCall = call;
      this.callStartTime = Date.now();
      this.incomingCallHandlers.forEach((handler) => {
        handler.onCallAccepted();
      });
    });

    call.on("disconnect", () => {
      this.activeCall = null;
      this.callStartTime = null;
      this.incomingCallHandlers.forEach((handler) => {
        handler.onCallDisconnected();
      });
    });

    call.on("error", (error) => {
      this.activeCall = null;
      this.incomingCallHandlers.forEach((handler) => {
        handler.onError(error);
      });
    });
  }

  private handleDeviceError(error: Error) {
    console.error("Device error:", error);
    this.incomingCallHandlers.forEach((handler) => {
      handler.onError(error);
    });
  }

  private async fetchTranscriptionWithRetry(
    callSid: string,
    maxRetries = 5,
    delayMs = 2000
  ) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(
          `Attempt ${attempt}/${maxRetries} to fetch transcription for CallSid: ${callSid}`
        );
        
        try {
          const response = await axios.get(`/transcription/${callSid}`);
          
          console.log("Call transcription:", response.data);
          return response.data;
        } catch (error: any) {
          if (error.response && error.response.status === 404) {
            console.log("Transcription not ready yet, waiting...");
            await new Promise((resolve) => setTimeout(resolve, delayMs));
            continue;
          }
          
          console.error("Failed to fetch transcription:", error);
          if (attempt === maxRetries) return null;
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      } catch (error) {
        console.error("Error fetching transcription:", error);
        if (attempt === maxRetries) return null;
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }
    console.log("Max retries reached, transcription not available");
    return null;
  }

  async makeCall(phoneNumber: string, countryCode?: string) {
    // Ensure device is initialized before making a call
    if (!this.device || this.device.state !== "registered") {
      console.log("Device not initialized, initializing now...");
      await this.initializeDevice();
    }

    if (!this.device || this.device.state !== "registered") {
      throw new Error("Failed to initialize device");
    }

    const formattedNumber = this.formatPhoneNumber(phoneNumber, countryCode);
    console.log("Making call to:", formattedNumber);

    this.activeCall = await this.device.connect({
        params: {
          To: formattedNumber,
          From: this.fromNumber,
          Direction: "outbound-api",
          CallerId: this.fromNumber,
          ApplicationSid: "APd84cfff0fdaa248c6b845487f3c837aa",
          Record: "true",
          RecordingChannels: "dual",
          RecordingStatusCallback: `${this.baseUrl}/api/scribing/calls/recording`,
          StatusCallback: "https://b264-2409-40f0-3083-4db2-2839-d9c4-1460-57ed.ngrok-free.app/api/calls/recording",
          StatusCallbackEvent: "initiated ringing answered completed no-answer busy failed",
          RecordingStatusCallbackMethod: "POST",
        },
      });


    // Add event listeners for call states
    this.activeCall.on("accept", () => {
      console.log(
        "Call accepted with CallSid:",
        this.activeCall?.parameters.CallSid
      );
      console.log(
        "You can use this CallSid to download the audio recording once the call is complete."
      );
    });

    this.activeCall.on("disconnect", async () => {
      const callSid = this.activeCall?.parameters.CallSid;
      console.log("Call disconnected. Final CallSid:", callSid);

      // Clean up the call state
      this.activeCall = null;
      this.callStartTime = null;

      // Notify all handlers about disconnection
      this.incomingCallHandlers.forEach((handler) => {
        handler.onCallDisconnected();
      });

      // Get transcription if we have a CallSid
      if (callSid) {
        // Wait a bit before starting to fetch transcription
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const transcription = await this.fetchTranscriptionWithRetry(callSid);
        if (!transcription) {
          console.log(
            "Could not retrieve transcription after multiple attempts"
          );
        }
      }
    });

    this.activeCall.on("error", (error) => {
      console.error("Call error:", error);
      // Clean up the call state
      this.activeCall = null;
      this.callStartTime = null;
      // Notify all handlers about the error
      this.incomingCallHandlers.forEach((handler) => {
        handler.onError(error);
      });
    });

    this.callStartTime = Date.now();
    return this.activeCall;
  }

  async acceptIncomingCall() {
    // Ensure device is initialized before accepting a call
    if (!this.device || this.device.state !== "registered") {
      console.log("Device not initialized, initializing now...");
      await this.initializeDevice();
    }

    if (!this.device) {
      throw new Error("Device not initialized");
    }

    if (this.activeCall) {
      await this.activeCall.accept();
    }
  }

  async rejectIncomingCall() {
    // Ensure device is initialized before rejecting a call
    if (!this.device || this.device.state !== "registered") {
      console.log("Device not initialized, initializing now...");
      await this.initializeDevice();
    }

    if (!this.device) {
      throw new Error("Device not initialized");
    }

    if (this.activeCall) {
      await this.activeCall.reject();
      this.activeCall = null;
    }
  }

  registerIncomingCallHandlers(handlers: IncomingCallHandlers) {
    this.incomingCallHandlers.add(handlers);
  }

  unregisterIncomingCallHandlers(handlers: IncomingCallHandlers) {
    this.incomingCallHandlers.delete(handlers);
  }

  private formatPhoneNumber(phoneNumber: string, countryCode?: string): string {
    // Remove any non-digit characters
    const digitsOnly = phoneNumber.replace(/\D/g, "");

    // If number already starts with country code (without +), just add the +
    if (countryCode && digitsOnly.startsWith(countryCode.slice(1))) {
      return `+${digitsOnly}`;
    }

    // If country code is provided, use it, otherwise default to +91
    const prefix = countryCode || "+91";
    return `${prefix}${digitsOnly}`;
  }

  getDeviceStatus() {
    if (!this.device) {
      return { ready: false };
    }

    return {
      ready: this.device.state === "registered",
      activeCall: !!this.activeCall,
    };
  }

  private logTwilioError(error: any) {
    console.error("=== Twilio Error Details ===");
    console.error("Error Code:", error.code);
    console.error("Error Message:", error.message);
    console.error(
      "Known Error:",
      TWILIO_ERROR_CODES[error.code] || "Unknown error type"
    );

    if (error.twilioError) {
      console.error("Twilio Specific Error:", error.twilioError);
    }

    if (error.info) {
      console.error("Additional Info:", error.info);
    }

    // Log connection details
    if (this.device) {
      console.error("Device Status:", {
        state: this.device.state,
        ready: this.device.state === "registered",
      });
    }

    console.error("=== End Error Details ===");
  }

  async getCallStatus(): Promise<CallStatus> {
    // For getCallStatus, we don't need to initialize the device
    // Just return an inactive status if the device isn't available
    if (!this.device) {
      return {
        isInitialized: false,
        isConnected: false,
        callSid: null,
        status: "completed",
        duration: null,
        error: null,
      };
    }

    return {
      isInitialized: true,
      isConnected: !!this.activeCall,
      callSid: this.activeCall?.parameters.CallSid || null,
      status: this.activeCall ? "in-progress" : "completed",
      duration: this.callStartTime
        ? `${Math.floor((Date.now() - this.callStartTime) / 1000)}s`
        : null,
      error: null,
    };
  }

  async endCall(): Promise<void> {
    // No need to initialize the device just to end a call
    // If there's no active call, there's nothing to end
    if (this.activeCall) {
      console.log("Ending call...");
      await this.activeCall.disconnect();
      this.activeCall = null;
      console.log("Call ended");
    } else {
      console.log("No active call to end");
    }
  }

  // Method to update token if needed
  async setToken(newToken: string) {
    console.log("Updating token...");
    // Force re-initialization with new token
    this.device = null;
    this.initializationPromise = null;
    await this.setupDevice(newToken);
    console.log("Token updated and device reinitialized");
  }

  // Clean up method
  destroy() {
    console.log("Cleaning up CallAPI...");
    if (this.activeCall) {
      this.activeCall.disconnect();
    }
    if (this.device) {
      this.device.destroy();
    }
    this.device = null;
    this.initializationPromise = null;
    console.log("Cleanup complete");
  }

  async initiateCall(
    phoneNumber: string,
    countryCode?: string
  ): Promise<CallResponse> {
    try {
      const call = await this.makeCall(phoneNumber, countryCode);
      return {
        call_sid: call.parameters.CallSid,
        status: "in-progress",
        duration: null,
      };
    } catch (error) {
      console.error("Error initiating call:", error);
      this.logTwilioError(error);
      throw error;
    }
  }

  public async toggleMute(muted: boolean): Promise<void> {
    // If we have no device or no active call, there's nothing to mute
    if (!this.device || !this.activeCall) {
      console.log("No active call to mute/unmute");
      return;
    }

    this.activeCall.mute(muted);
  }
}

export { CallAPI };
export const callAPI = new CallAPI();
